import type { Action, Draft, PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { IUser } from 'Model/index.js'
import * as Sentry from '@sentry/react'
import { removeRequest, removeRequestByType } from 'Flux/FluxUtils.ts'
import { setTelemetryOrg, trackAuth } from 'Services/Telemetry/index.ts'

/** Push the authenticated user's org into telemetry so every metric is segmentable by organisation. */
/**
 * Why a login attempt ended without a user.
 *  - `unauthenticated`: no session — the visitor is being redirected to Keycloak to sign in.
 *  - `invalid_session`: the profile came back but is unusable (no email).
 */
export type LoginFailureReason = 'unauthenticated' | 'invalid_session'

const syncTelemetryOrg = (user?: IUser) => {
  setTelemetryOrg(user?.org ? { id: user.org.id, type: user.org.type, isInternal: user.org.isInternal } : null, user?.id)
}

export interface IAuthenticationState {
  user?: IUser
  openRequests: string[]
  userWasFetched: boolean
  /** Set by a 401 until the user is known again; consumers wait instead of refetching into a 401 loop. */
  sessionExpired: boolean
}

const initialState: IAuthenticationState = {
  user: undefined,
  openRequests: [],
  userWasFetched: false,
  sessionExpired: false
}

const reducers = {
  updateUserIfPresent: (state: Draft<IAuthenticationState>, action: Action) => {
    state.openRequests.push(action.type)
  },
  fetchUserIfPresentSuccess: (state: Draft<IAuthenticationState>, action: PayloadAction<{ user?: IUser }>) => {
    state.user = action.payload.user
    Sentry.setUser({
      ...(action?.payload?.user ?? {}),
      username: action?.payload?.user?.email
    })
    syncTelemetryOrg(action.payload.user)
    state.userWasFetched = true
    state.sessionExpired = false
    state.openRequests = removeRequest(state, action)
  },
  updateUserIfPresentFailure: (state: Draft<IAuthenticationState>, action: Action) => {
    state.userWasFetched = true
    state.openRequests = removeRequest(state, action)
  },
  loginRequest: (state: Draft<IAuthenticationState>, action: Action) => {
    state.openRequests.push(action.type)
  },
  loginSuccess: (state: Draft<IAuthenticationState>, action: PayloadAction<{ user: IUser }>) => {
    state.user = action.payload.user
    Sentry.setUser({
      ...(action?.payload?.user ?? {}),
      username: action?.payload?.user?.email
    })
    syncTelemetryOrg(action.payload.user)
    trackAuth('login_success')
    state.sessionExpired = false
    state.openRequests = removeRequest(state, action)
  },
  loginFailure: (state: Draft<IAuthenticationState>, action: PayloadAction<{ error: Error; reason?: LoginFailureReason }>) => {
    // `unauthenticated` is the ordinary "not signed in yet" path: LoginEpic has already sent the
    // visitor to Keycloak and this reducer only closes the open request. Recording it as a failure
    // is what produced a phantom ~50% login-failure rate in the metrics.
    trackAuth(action.payload.reason === 'unauthenticated' ? 'login_redirect' : 'login_failed')
    state.openRequests = removeRequest(state, action)
  },
  acceptInvitationRequest: (
    state: Draft<IAuthenticationState>,
    action: PayloadAction<{
      invitationId: string
      password: string
    }>
  ) => {
    state.openRequests.push(action.type)
  },
  acceptInvitationSuccess: (state: Draft<IAuthenticationState>) => {
    trackAuth('invitation_accepted')
    state.openRequests = state.openRequests.filter((request) => request !== authenticationSlice.actions.acceptInvitationRequest.type)
  },
  acceptInvitationFailure: (state: Draft<IAuthenticationState>) => {
    state.openRequests = state.openRequests.filter((request) => request !== authenticationSlice.actions.acceptInvitationRequest.type)
  },
  authenticationExpired: (
    state: Draft<IAuthenticationState>,
    action: PayloadAction<{ failedRequest: string; message?: string; showAlways?: boolean }>
  ) => {
    state.sessionExpired = true
    state.openRequests = removeRequestByType(state, action.payload.failedRequest)
  },
  reset: () => {
    syncTelemetryOrg(undefined)
    return initialState
  }
}

export const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers
})
